import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { X } from "lucide-react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "../misc/Dialog";
import { Label } from "../misc/Label";
import { Button } from "../misc/Button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../misc/Select";
import CustomPosts from "../misc/custom-posts/CustomPosts";
import type { ListingList } from "../post/types";
import type { UserListing } from "../../types/userListing";
import { getAuthHeader } from "../../utils/authHeader";
import { isPostableRow, isPropertyRow, presetDialogTitle } from "./preset";
import { resolveDeepLinkListing } from "./rules/deepLinks";
import { useCloseAddPostCard } from "./useAddPostNavigation";
import { openHouseNote } from "./rules/openHouseNote";

interface Props {
    card: "open-house" | "video-showcase";
    rows: ListingList[];
    rowsLoading: boolean;
    rowsError: boolean;
    /** Whether any listing is on the account (null = unknown): picks the note when there is no campaign to offer. */
    hasListings: boolean | "checking" | null;
    campaignId: string | null;
}

const rowValue = (r: ListingList): string => `${r.campaignId}|||${r.listingType}`;

/**
 * Open House and Video Showcase from the grid: pick one of the ACTIVE property campaigns (a paused one
 * cannot publish these kinds), then today's dialog opens for the loaded listing.
 */
const ListingPickDialog: React.FC<Props> = ({ card, rows, rowsLoading, rowsError, hasListings, campaignId }) => {
    const closeCard = useCloseAddPostCard();
    const propertyRows = useMemo(() => rows.filter((r) => isPropertyRow(r) && isPostableRow(r)), [rows]);
    const [picked, setPicked] = useState<ListingList | undefined>(() => resolveDeepLinkListing(propertyRows, campaignId));
    const [listing, setListing] = useState<UserListing | null>(null);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);
    // The rows usually arrive after the first render; the deep link is applied once they do.
    const deepLinkApplied = useRef(picked !== undefined);
    useEffect(() => {
        if (deepLinkApplied.current || !campaignId) return;
        const found = resolveDeepLinkListing(propertyRows, campaignId);
        if (found) {
            deepLinkApplied.current = true;
            setPicked(found);
        }
    }, [propertyRows, campaignId]);
    // The wizard calls getAllPosts when posts were created: on success (then it closes itself) and on a
    // partial failure (then it stays open with its error and Retry). getAllPosts only records the fact;
    // the navigation happens on the close that follows.
    const scheduledRef = useRef(false);

    const toGrid = (): void => closeCard(false);

    const load = async (): Promise<void> => {
        if (!picked) return;
        setLoading(true);
        setFailed(false);
        try {
            const headers = await getAuthHeader();
            const res = await axios.get<UserListing>(`${process.env.REACT_APP_BACKEND_URL}/listing/${picked.id}`, { headers });
            setListing(res.data);
        } catch {
            setFailed(true);
        } finally {
            setLoading(false);
        }
    };

    if (listing && picked) {
        return (
            <CustomPosts
                key={picked.id}
                open
                listing={listing}
                isOpenHouse={card === "open-house"}
                isVideo={card === "video-showcase"}
                onBackFromStart={() => setListing(null)}
                getAllPosts={() => {
                    scheduledRef.current = true;
                }}
                setIsOpen={(open) => {
                    if (!open) closeCard(scheduledRef.current);
                }}
            />
        );
    }

    const note = card === "open-house" ? openHouseNote(picked) : null;
    const rowsEmpty = propertyRows.length === 0;
    const allPaused = rowsEmpty && rows.some(isPropertyRow);

    return (
        <Dialog open onOpenChange={(open) => { if (!open) toGrid(); }}>
            <DialogContent className="bg-white relative flex flex-col space-y-6 p-4" aria-describedby={undefined}>
                <button type="button" aria-label="Close" onClick={toGrid} className="absolute right-4 top-4 text-gray-500 hover:text-gray-700" style={{ zIndex: 999 }}>
                    <X className="h-5 w-5" />
                </button>
                <DialogTitle className="px-6 pt-2 text-center text-xl font-semibold leading-normal tracking-normal text-primary">{presetDialogTitle(card)}</DialogTitle>
                <div className="flex flex-col gap-2 px-6">
                    <Label htmlFor="add-post-listing-pick" className="text-primary font-semibold">Which listing?</Label>
                    <Select
                        value={picked ? rowValue(picked) : ""}
                        onValueChange={(v) => setPicked(propertyRows.find((r) => rowValue(r) === v))}
                        disabled={loading}
                    >
                        <SelectTrigger id="add-post-listing-pick">
                            <SelectValue placeholder="Select a listing" />
                        </SelectTrigger>
                        <SelectContent>
                            {propertyRows.map((r) => (
                                <SelectItem key={rowValue(r)} value={rowValue(r)}>
                                    {r.displayName || r.address || "Listing"}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {rowsLoading && rowsEmpty && <p className="text-sm text-gray-500">Loading your campaigns…</p>}
                    {!rowsLoading && rowsEmpty && rowsError && <p className="text-sm text-red-500">Your listings could not be loaded.</p>}
                    {!rowsLoading && allPaused && <p className="text-sm text-gray-500">Your listing campaigns are paused. Resume one to post here.</p>}
                    {!rowsLoading && rowsEmpty && !rowsError && !allPaused && hasListings === "checking" && (
                        <div role="status" aria-label="Checking your listings" className="h-5 w-2/3 rounded bg-gray-200 animate-skeleton" />
                    )}
                    {!rowsLoading && rowsEmpty && !rowsError && !allPaused && hasListings !== "checking" && (
                        <p className="text-sm text-gray-500">
                            {/* Own element: the note reads as one string, apart from the " · Listings" that follows it. */}
                            <span>{hasListings === true ? "Create a campaign on an active listing first." : "Add a listing and create a campaign first."}</span> · <Link to="/listings" className="underline">Listings</Link>
                        </p>
                    )}
                    {note && note.kind !== "none" && picked && (
                        <p className="text-sm text-gray-600">
                            {note.text}
                            {note.kind === "sync-off" && note.link === "campaign-settings" && (
                                <>
                                    {" "}
                                    <Link to={`/listing?id=${picked.id}`} className="font-semibold underline">Campaign settings</Link>
                                </>
                            )}
                            {note.kind === "sync-off" && note.link === "autopilot-settings" && (
                                <>
                                    {" "}Auto Pilot manages this campaign; turn on MLS Open House Sync in{" "}
                                    <Link to="/settings/autopilot" className="font-semibold underline">Auto Pilot settings</Link>.
                                </>
                            )}
                        </p>
                    )}
                    {loading && <p className="text-sm text-gray-500">Loading the listing…</p>}
                    {failed && !loading && <p className="text-sm text-red-500">The listing could not be loaded. Try again.</p>}
                </div>
                <div className="flex justify-end px-6 pb-4">
                    <Button onClick={() => void load()} disabled={!picked || loading}>Continue</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};
export default ListingPickDialog;
